/**
 * ConnectivityContext — single shared connection-status state.
 *
 * Replaces the module-level singleton in hooks/useConnectivity.js. Living as a
 * React context guarantees every consumer re-renders together when the state
 * changes (no stale subscribers across hot reloads).
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { useRealtime } from './RealtimeContext';

const ConnectivityContext = createContext({ online: true, mode: 'online', isOffline: false });

export const ConnectivityProvider = ({ children }) => {
  const { status } = useRealtime();
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  let mode = 'online';
  if (!online) mode = 'offline';
  else if (status !== 'open') mode = 'reconnecting';

  return (
    <ConnectivityContext.Provider value={{ online, mode, wsStatus: status, isOffline: mode === 'offline', isDegraded: mode !== 'online' }}>
      {children}
    </ConnectivityContext.Provider>
  );
};

export const useConnectivityCtx = () => useContext(ConnectivityContext);
